import React, { useEffect } from 'react';
import { createPortal } from 'react-dom';

interface DismissFromFeedPopupProps {
  isOpen: boolean;
  startupName: string;
  onKeep: () => void;
  onRemove: () => void;
}

const DismissFromFeedPopup: React.FC<DismissFromFeedPopupProps> = ({
  isOpen,
  startupName,
  onKeep,
  onRemove,
}) => {
  useEffect(() => {
    if (!isOpen) return;
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onKeep();
    };
    window.addEventListener('keydown', handleEscape);
    return () => window.removeEventListener('keydown', handleEscape);
  }, [isOpen, onKeep]);

  const handleRemove = () => {
    onRemove();
    onKeep();
  };

  const handleBackdropClick = (event: React.MouseEvent) => {
    if (event.target === event.currentTarget) onKeep();
  };

  if (!isOpen) return null;

  return createPortal(
    <div
      className="fixed inset-0 z-[100000] flex items-center justify-center bg-black/60 p-4"
      onClick={handleBackdropClick}
      role="dialog"
      aria-modal="true"
      aria-labelledby="dismiss-from-feed-popup-title"
    >
      <div
        className="relative max-h-[90vh] w-full max-w-lg overflow-y-auto rounded-2xl border border-gray-700 p-6 shadow-xl"
        style={{ backgroundColor: '#1E1E1C', fontFamily: 'Rubik, sans-serif' }}
        onClick={event => event.stopPropagation()}
      >
        <h2
          id="dismiss-from-feed-popup-title"
          className="mb-2 text-center text-lg font-semibold text-white"
        >
          Remove {startupName} from your deal flow?
        </h2>
        <p className="mb-5 text-center text-sm text-gray-300">
          They will no longer appear in Specially for you and other dashboard rows. You can still
          find them in Previously viewed.
        </p>

        <div className="flex flex-col gap-3">
          <button
            type="button"
            onClick={onKeep}
            className="w-full rounded-lg border-2 border-emerald-700 px-5 py-2.5 font-medium text-white transition-colors hover:bg-emerald-700 focus:outline-none focus:ring-2 focus:ring-emerald-700 focus:ring-offset-2 focus:ring-offset-[#1E1E1C]"
          >
            Oops, <span className="font-bold">keep this startup</span>
          </button>
          <button
            type="button"
            onClick={handleRemove}
            className="w-full rounded-lg border-2 border-red-500 px-5 py-2.5 font-medium text-white transition-colors hover:bg-red-500 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2 focus:ring-offset-[#1E1E1C]"
          >
            Remove
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default DismissFromFeedPopup;
